// frontend/src/features/ups/UpsDashboard.js
// UPS Admin dashboard layout:
// - Collapsible sidebar navigation
// - Global search & filters (scoped to driver views)
// - Stats + GlobalResultsDrawer shown only inside the drivers section
//
// Option A (canonical index URL):
// - Active tab is the index route: /ups-admin/drivers
// - /ups-admin/drivers/active may exist as an alias, but we treat /drivers as canonical.

import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  LayoutList,
  Ban as BannedIcon,
  Users,
  Building2,
  Menu,
  X,
  UserCheck,
  AlertTriangle,
  Inbox,
  MapPin,
} from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import UpsDriverStats from './UpsDriverStats';

// Phase 7: site selector load when entering drivers section
import { loadMyCompanySites } from '../sites/sitesSlice';

// Global filters (used by GlobalResultsDrawer + driver list tabs)
import { selectAgencyId, selectFilters, setScope, setQuery } from '../ui/filtersSlice';

// Global search drawer (results open in correct tab)
import GlobalResultsDrawer from '../../components/GlobalResultsDrawer';

// Maps "row.status" from GlobalResultsDrawer to the correct tab route.
const statusToRoute = {
  Requests: '/ups-admin/drivers/requests',
  Active: '/ups-admin/drivers', // ✅ Option A canonical
  Reserved: '/ups-admin/drivers/reserved',
  Banned: '/ups-admin/drivers/banned',
};

export default function UpsDashboard() {
  const [collapsed, setCollapsed] = useState(false);
  const btnBase = 'block w-full text-left px-4 py-2 rounded transition-all';

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const agencyId = useSelector(selectAgencyId);
  const { q } = useSelector(selectFilters);
  const isUpsAdmin = useSelector(selectIsUpsAdmin);

  // We only show driver stats + global drawer when user is in the drivers section.
  const matchDriversRoot = useMatch('/ups-admin/drivers');
  const matchDriversAny = useMatch('/ups-admin/drivers/*');
  const inDriversSection = Boolean(matchDriversRoot || matchDriversAny);

  // Load company sites when entering drivers section (SiteSelector depends on it)
  useEffect(() => {
    if (!inDriversSection) return;
    dispatch(loadMyCompanySites());
  }, [dispatch, inDriversSection]);

  // Clear global search + scope when leaving the drivers section
  useEffect(() => {
    if (!inDriversSection) {
      dispatch(setScope('tab'));
      dispatch(setQuery(''));
    }
  }, [inDriversSection, dispatch]);

  const openResultInTab = (row) => {
    const route = statusToRoute[row.status] || '/ups-admin/drivers';
    dispatch(setScope('tab'));

    const params = new URLSearchParams();
    if (agencyId) params.set('agencyId', agencyId);
    if (q) params.set('q', q);

    // Optional focus behavior (if list components support it)
    params.set('focus', String(row.id));

    navigate(`${route}?${params.toString()}`);
  };

  const viewAllForStatus = (status) => {
    const route = statusToRoute[status] || '/ups-admin/drivers';
    dispatch(setScope('tab'));

    const params = new URLSearchParams();
    if (agencyId) params.set('agencyId', agencyId);
    if (q) params.set('q', q);

    navigate(`${route}?${params.toString()}`);
  };

return (
  <div className="flex min-h-screen bg-gray-50">
    {/* Sidebar */}
    <aside
      className={`${
        collapsed ? 'w-16' : 'w-64'
      } bg-white border-r shadow-sm flex flex-col transition-all duration-200`}
    >
      <div className="p-4 flex items-center justify-between">
        {!collapsed && <h2 className="text-xl font-bold">UPS Admin</h2>}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded hover:bg-gray-200"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
      </div>

      <nav className="flex-1 space-y-1 px-1">
        {/* ---------------- Operations ---------------- */}

        <NavLink
          to="/ups-admin/drivers"
          end
          className={({ isActive }) =>
            `${btnBase} ${
              isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            } flex items-center`
          }
        >
          <LayoutList className="mr-2" />
          {!collapsed && 'Active Drivers'}
        </NavLink>

        <NavLink
          to="/ups-admin/drivers/reserved"
          className={({ isActive }) =>
            `${btnBase} ${
              isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            } flex items-center`
          }
        >
          <UserCheck className="mr-2" />
          {!collapsed && 'Reserved Drivers'}
        </NavLink>

        <NavLink
          to="/ups-admin/drivers/requests"
          className={({ isActive }) =>
            `${btnBase} ${
              isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            } flex items-center`
          }
        >
          <Inbox className="mr-2" />
          {!collapsed && 'Requests'}
        </NavLink>

        <NavLink
          to="/ups-admin/drivers/banned"
          className={({ isActive }) =>
            `${btnBase} ${
              isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            } flex items-center`
          }
        >
          <BannedIcon className="mr-2" />
          {!collapsed && 'Banned Drivers'}
        </NavLink>

        {isUpsAdmin && (
          <NavLink
            to="/ups-admin/drivers/blocked"
            className={({ isActive }) =>
              `${btnBase} ${
                isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
              } flex items-center`
            }
          >
            <AlertTriangle className="mr-2" />
            {!collapsed && 'Blocked / Orphaned'}
          </NavLink>
        )}

        {/* ---------------- Divider ---------------- */}

        {!collapsed && (
          <div className="mt-6 mb-2 px-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Management
            </div>
            <div className="mt-2 border-t border-gray-200"></div>
          </div>
        )}

        {/* ---------------- Management ---------------- */}

        <NavLink
          to="/ups-admin/manage-sites"
          className={({ isActive }) =>
            `${btnBase} ${
              isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            } flex items-center`
          }
        >
          <MapPin className="mr-2" />
          {!collapsed && 'Manage Sites'}
        </NavLink>

        <NavLink
          to="/ups-admin/manage-agencies"
          className={({ isActive }) =>
            `${btnBase} ${
              isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            } flex items-center`
          }
        >
          <Building2 className="mr-2" />
          {!collapsed && 'Manage Agencies'}
        </NavLink>

        <NavLink
          to="/ups-admin/accounts"
          className={({ isActive }) =>
            `${btnBase} ${
              isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            } flex items-center`
          }
        >
          <Users className="mr-2" />
          {!collapsed && 'Manage Accounts'}
        </NavLink>
      </nav>
    </aside>

    {/* Main content */}
    <main className="relative flex-1 overflow-y-auto">
      <div className="p-6">
        {inDriversSection && <UpsDriverStats />}
        {inDriversSection && (
          <GlobalResultsDrawer onOpen={openResultInTab} onViewAll={viewAllForStatus} />
        )}
        <Outlet />
      </div>
    </main>
  </div>
);}
